import { ReportingDefaultReportSets } from '../../../../i18n'
import {
    agentsURL,
    AGENT_ADMIN_LIST_FIELDS,
    CACHE_TIME,
    callRecordingIntegrationsURL,
    defaultReportsListURL,
    DEVICE_ADMIN_LIST_FIELDS,
    directoryEntryListURL,
    directoryListForCMEURL,
    devicesAdminListURL,
    devicesURL,
    EXTENDED_CACHE_TIME,
    getAllTelSysAccessibleByUser,
    getClientConfigurationListURL,
    getCMEURL,
    getCRMConfigurationListURL,
    getFilterEntityTypesURL,
    getSimulationModesURL,
    licenceManagementListURL,
    groupMembershipURL,
    LOCALES_URL,
    NO_CACHE_TIME,
    partitionsURL,
    partitionURL,
    telServerURL,
    codeListURL,
    recordingListURL,
    CODE_ADMIN_LIST_FIELDS,
    RECORDING_ADMIN_LIST_FIELDS,
    cmeListURL,
    CME_ADMIN_LIST_FIELDS,
    PARTITION_ADMIN_LIST_FIELDS,
    partitionAdminListURL,
    telSysListURL,
    TelSysFields,
    TELSYS_ADMIN_LIST_FIELDS,
    TelsysListAPIFilters,
    telSysURL,
    changeLogListURL,
    type ChangeLogListQueryParams,
    logManagementMutationURL,
    loggingSettingsURL,
    logManagementStatusURL,
    type LogManagementScopeQueryParams,
    type LogManagementStatusQueryParams,
    telModuleStatusURL,
    type TelModuleStatusQueryParams,
    USER_ADMIN_LIST_FIELDS,
    usersAdminListURL,
    usersURLByTelSysPartition
} from '../../../NonReact/constants/ApiConstants'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { PartitionValues } from '../../AddUser/Types'
import { SelectOptions } from '../Components/Selects'
import {
    AgentSelectItem,
    DeviceSelectItem,
    EntitySelectOption,
    SelectItem,
    SelectOption
} from '../Components/Selects/Types'
import {
    Agent,
    CallRecordingIntegration,
    CME,
    DefaultReportItem,
    Device,
    GenericAgentsCollections,
    GenericDeviceCollections,
    IndividualTelItem,
    LocaleEntries,
    Partition,
    ReportEntityTypes,
    TelSys
} from '../Types/Types'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { PARTITION_LICENCES } from '../../../NonReact/constants/TelSysConstants'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { ClientConfigurationsResponse } from '../../../NonReact/Types/ClientConfigurationTypes'
import { LicenceManagementListResponse } from '../../../NonReact/Types/LicenceManagementPageTypes'
import { ANY_DEVICE_VALUE } from '../Constants/DefaultValues'
import { COMMA, EMPTY_STRING } from '../Constants/Separators'
import {
    AdminUserResponse,
    APIData,
    APIListData,
    InternalUserResponse,
    JSONResponse
} from '../../../NonReact/common/Types/Types'
import { CRMConfigurationsResponse } from '../../../NonReact/Types/CRMConfiguration/Types'
import { AgentListResponse } from '../../../NonReact/Types/AgentListTypes'
import { CmeListResponse } from '../../../NonReact/Types/CmeListTypes'
import { CodeListResponse } from '../../../NonReact/Types/CodeListTypes'
import { RecordingListResponse } from '../../../NonReact/Types/RecordingListTypes'
import { DirectoryListResponse } from '../../../NonReact/Types/DirectoryListTypes'
import { TelsysListResponse } from '../../../NonReact/Types/TelSysListTypes'
import { PartitionListResponse } from '../../../NonReact/Types/PartitionListTypes'
import { UsersListResponse } from '../../../NonReact/Types/UsersListTypes'
import { DeviceListResponse } from '../../../NonReact/Types/DeviceListTypes'
import { ConfigChangeLogListResponse } from '../../../NonReact/Types/ConfigChangeLogListTypes'
import { LogManagementListResponse } from '../../../NonReact/Types/LogManagementListTypes'
import { LoggingSettingsPutRequest, LoggingSettingsResponse } from '../../../NonReact/Types/LoggingSettingsTypes'
import { TelModuleStatusListResponse } from '../../../NonReact/Types/TelModuleStatusListTypes'

export const ALL_VALUE: number = -1
export const INVALID_VALUE: number = -3
export const EMPTY_VALUE: string = ''
export const PRIMARY_PARTITION: number = 1

export const ALL_TEL_OPTION: SelectOption<number> = {
    label: '[All Telephony Servers]',
    value: ALL_VALUE
}

// Get the list of CMEs.
export const getCMEsAsSelectOptions = (
    fields?: string[],
    filterFunction?: (cme: CME) => boolean
): Promise<SelectOptions<string>> =>
    requestGetJSON<APIListData<CME>>(getCMEURL(fields), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<CME>>): SelectOptions<string> => {
            const availableCMEs: SelectOptions<string> =
                res?.Data?.Items?.filter(filterFunction ?? ((): boolean => true))?.map(
                    ({ Description, CMEnvironmentID }: CME): SelectOption<string> => ({
                        label: Description,
                        value: CMEnvironmentID
                    })
                ) ?? []

            return availableCMEs
        }
    )

// Get the list of Tel Servers.
export const getTelServersAsSelectOptionsWithCMEScopeCheck = (
    cmeID: string,
    recursive?: boolean,
    userInfo?: AdminUserResponse,
    telSysFields?: string[],
    ...filterByLicenceLevel: ReportingAccessLevels[]
): Promise<EntitySelectOption<number, TelSys>[]> =>
    requestGetJSON<APIListData<TelSys>>(telServerURL(cmeID, recursive, false, telSysFields, ...filterByLicenceLevel), {
        errorOnNoContent: false,
        cacheTime: CACHE_TIME
    }).then((res: JSONResponse<APIListData<TelSys>>): EntitySelectOption<number, TelSys>[] => {
        const availableServers: EntitySelectOption<number, TelSys>[] =
            res?.Data?.Items?.map(
                ({
                    Description,
                    TelSysID,
                    TelSysTypeID,
                    PartitionLicences
                }: TelSys): EntitySelectOption<number, TelSys> => {
                    let label: string = EMPTY_STRING

                    if (!IsStringEmpty(Description)) {
                        label = Description
                    } else if (isVarNotEmpty(TelSysID)) {
                        label = String(TelSysID)
                    }

                    return {
                        label,
                        value: TelSysID,
                        original: { Description, TelSysID, PartitionLicences, TelSysTypeID }
                    }
                }
            ) ?? []

        return userInfo && userInfo.Scope.TelSysID.includes(ALL_VALUE)
            ? [ALL_TEL_OPTION, ...availableServers]
            : availableServers
    })

// Get the list of Tel Servers.
export const getTelServersAsSelectOptions = (
    cmeID: string,
    recursive?: boolean,
    errorOnNoContent: boolean = true,
    ...licenceLevel: string[]
): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(
        telServerURL(
            cmeID,
            recursive,
            false,
            [TelSysFields.PARTITION_LICENCES, TelSysFields.TELSYS_TYPE, TelSysFields.DESCRIPTION],
            ...licenceLevel
        ),
        {
            cacheTime: CACHE_TIME,
            errorOnNoContent
        }
    ).then((res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
        const availableServers: SelectOptions<number> =
            res?.Data?.Items?.map(
                ({ Description, TelSysID, PartitionLicences, TelSysType }: TelSys): SelectOption<number> => ({
                    label: Description ?? String(TelSysID),
                    value: TelSysID,
                    licences: PartitionLicences,
                    telSysType: TelSysType
                })
            ) ?? []

        return availableServers
    })

// Get the list of Tel Servers - CXI Filter
export const getTelServersAsSelectOptionsWithCXIFilter = (
    cmeID: string,
    recursive?: boolean,
    cxiTelSysOnly?: boolean,
    errorOnNoContent: boolean = true,
    ...licenceLevel: string[]
): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(
        telServerURL(
            cmeID,
            recursive,
            cxiTelSysOnly,
            [TelSysFields.PARTITION_LICENCES, TelSysFields.TELSYS_TYPE],
            ...licenceLevel
        ),
        {
            cacheTime: CACHE_TIME,
            errorOnNoContent
        }
    ).then((res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
        const availableServers: SelectOptions<number> =
            res?.Data?.Items?.map(
                ({ Description, TelSysID, PartitionLicences, TelSysType }: TelSys): SelectOption<number> => ({
                    label: Description,
                    value: TelSysID,
                    licences: PartitionLicences,
                    telSysType: TelSysType
                })
            ) ?? []

        return availableServers
    })

export const getTelServersAsSelectOptionsWithFilters = (
    cmeID: string,
    filteredTelSysTypes: TelSysTypes[],
    recursive?: boolean,
    ...licenceLevel: string[]
): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(
        telServerURL(
            cmeID,
            recursive,
            false,
            [TelSysFields.PARTITION_LICENCES, TelSysFields.TELSYS_TYPE],
            ...licenceLevel
        ),
        {
            cacheTime: CACHE_TIME,
            errorOnNoContent: true
        }
    ).then((res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
        const availableServers: SelectOptions<number> =
            res?.Data?.Items?.filter((telSys: TelSys): boolean =>
                isVarNotEmpty(telSys?.TelSysType) ? filteredTelSysTypes.includes(telSys?.TelSysType) : true
            )?.map(
                ({ Description, TelSysID, PartitionLicences }: TelSys): SelectOption<number> => ({
                    label: Description ?? String(TelSysID),
                    value: TelSysID,
                    licences: PartitionLicences
                })
            ) ?? []

        return availableServers
    })

// Get all tel servers for current user and return them as a list of SelectOptions.
export const getAllTelServersAsSelectOptions = (): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(getAllTelSysAccessibleByUser(), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
            const availableServers: SelectOptions<number> =
                res?.Data?.Items?.map(
                    ({ Description, TelSysID }: TelSys): SelectOption<number> => ({
                        label: Description ?? String(TelSysID),
                        value: TelSysID
                    })
                ) ?? []

            return availableServers
        }
    )

// Get a list of partitions for a given tel server.
export const getPartitionsForTelServer = (telServerID: number): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<Partition>>(partitionsURL(telServerID), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<Partition>>): SelectOptions<number> => {
            const availablePartitions: SelectOptions<number> =
                res?.Data?.Items?.map(
                    ({ Description, PartIDInTelSys, LicensedForRptUserType }: Partition): SelectOption<number> => ({
                        label: Description ?? String(PartIDInTelSys),
                        value: PartIDInTelSys,
                        licencedForRptType: LicensedForRptUserType
                    })
                ) ?? []

            return availablePartitions
        }
    )

// Helper function for getting devices with a specified cache time
const fetchDevices = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    cacheTime?: number,
    limit?: number,
    offset?: number,
    searchQuery?: string,
    ...deviceFields: string[]
): Promise<[DeviceSelectItem[], Definable<number>]> => {
    const devicesPromise: Promise<JSONResponse<APIData<GenericDeviceCollections>>> = device
        ? requestGetJSON<APIData<GenericDeviceCollections>>(
              groupMembershipURL(
                  telServerID,
                  partition,
                  device,
                  deviceTypeFilter,
                  searchQuery,
                  deviceFields.join(COMMA),
                  limit,
                  offset
              ),
              { cacheTime }
          )
        : requestGetJSON<APIData<GenericDeviceCollections>>(
              devicesURL(
                  telServerID.toString(),
                  partition.toString(),
                  monitoredOnly,
                  deviceTypeFilter,
                  deviceFields,
                  searchQuery,
                  undefined,
                  limit,
                  offset
              ),
              { cacheTime }
          )

    return devicesPromise.then(
        (res: JSONResponse<APIData<GenericDeviceCollections>>): [DeviceSelectItem[], Definable<number>] => {
            const devices: Nullable<Device[]> = res?.Data?.Items ?? res?.Data?.Devices
            const availableDevices: DeviceSelectItem[] =
                devices?.map(
                    (
                        {
                            Description,
                            DeviceNumber,
                            DeviceTypeID,
                            Users,
                            TelSysUsername,
                            IDOnTelSys,
                            TelSysUserID,
                            ACDAgentNumber,
                            OverrideMonitoring,
                            LicenseType,
                            QueueStatus,
                            DisableRTMonitoring,
                            QueueCompatibility,
                            IDOnTelSysOwner,
                            CallQueueCaptureErrorMessage,
                            PrimaryEmail
                        }: Device,
                        index: number
                    ): DeviceSelectItem => ({
                        label: `${DeviceNumber} - ${Description}`,
                        value: DeviceNumber,
                        type: DeviceTypeID?.toString(),
                        original: {
                            Description,
                            DeviceNumber,
                            DeviceTypeID: DeviceTypeID ?? 0,
                            Users,
                            TelSysUsername,
                            IDOnTelSys,
                            TelSysUserID,
                            ACDAgentNumber,
                            OverrideMonitoring,
                            LicenceType: LicenseType,
                            QueueStatus,
                            DisableRTMonitoring,
                            QueueCompatibility,
                            IDOnTelSysOwner,
                            CallQueueCaptureErrorMessage,
                            PrimaryEmail
                        },
                        isAgent: !!ACDAgentNumber,
                        index
                    })
                ) ?? []

            const total: Definable<number> = res?.Data?.Total ?? res?.Data?.TotalDevices ?? devices?.length
            return [availableDevices, total]
        }
    )
}

// Get a list of devices, with default cache time.
export const getDevicesFromPartition = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    limit?: number,
    offset?: number,
    searchQuery?: string,
    ...deviceFields: string[]
): Promise<[DeviceSelectItem[], Definable<number>]> =>
    fetchDevices(
        telServerID,
        partition,
        monitoredOnly,
        deviceTypeFilter,
        device,
        CACHE_TIME,
        limit,
        offset,
        searchQuery,
        ...deviceFields
    )

// Get a list of devices, with a specified cache time.
export const getDevicesFromPartitionByCache = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    limit?: number,
    offset?: number,
    cacheTime: number = CACHE_TIME,
    ...deviceFields: string[]
): Promise<[DeviceSelectItem[], Definable<number>]> =>
    fetchDevices(
        telServerID,
        partition,
        monitoredOnly,
        deviceTypeFilter,
        device,
        cacheTime,
        limit,
        offset,
        undefined,
        ...deviceFields
    )

// Get a list of agents.
export const getAgentsFromPartition = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    ...agentFields: string[]
): Promise<AgentSelectItem[]> => {
    /* eslint-disable */
    const agentsPromises: Promise<JSONResponse<APIData<GenericAgentsCollections>>> = device
        ? requestGetJSON<APIData<GenericAgentsCollections>>(
            groupMembershipURL(telServerID, partition, device, deviceTypeFilter),
            { cacheTime: CACHE_TIME }
        )
        : requestGetJSON<APIData<GenericAgentsCollections>>(
            agentsURL(`${telServerID}`, `${partition}`, monitoredOnly, agentFields, undefined, undefined, undefined, undefined),
            {
                cacheTime: CACHE_TIME
            }
        )
    /* eslint-enable */

    return agentsPromises.then((res: JSONResponse<APIData<GenericAgentsCollections>>): SelectItem[] => {
        const agents: Nullable<Agent[]> = res?.Data?.Items ?? res?.Data?.ACDAgents
        const availableAgents: AgentSelectItem[] =
            agents?.map(
                ({
                    Description,
                    AgentNumber,
                    IDOnTelSys,
                    TelSysUsername,
                    OverrideMonitoring
                }: Agent): AgentSelectItem => ({
                    label: `${AgentNumber} - ${Description}`,
                    value: AgentNumber,
                    original: { Description, AgentNumber, IDOnTelSys, TelSysUsername, OverrideMonitoring }
                })
            ) ?? []

        return availableAgents
    })
}

// Get a list of call recording integrations.
export const getCallRecordingIntegrations = (
    cmeID: string,
    telServerID: number,
    partitionID: number
): Promise<SelectOptions<string>> =>
    // eslint-disable-next-line
    requestGetJSON<CallRecordingIntegration[]>(callRecordingIntegrationsURL(cmeID, telServerID, partitionID), {
        cacheTime: EXTENDED_CACHE_TIME
    }).then((res: JSONResponse<CallRecordingIntegration[]>): SelectOptions<string> => {
        if (!Array.isArray(res)) {
            return []
        }

        const availableCRIs: SelectOptions<string> =
            res?.map(
                (cri: CallRecordingIntegration): SelectOption<string> => ({
                    label: cri.Description,
                    value: cri.ID
                })
            ) ?? []

        return availableCRIs
    })

// Get a telephony server.
export const getTelephonyServer = (
    telServerID: number | string,
    ...telSysFields: string[]
): Promise<Nullable<IndividualTelItem>> =>
    requestGetJSON<APIData<IndividualTelItem>>(telSysURL(Number(telServerID), PARTITION_LICENCES, ...telSysFields), {
        cacheTime: CACHE_TIME
    }).then((res: JSONResponse<APIData<IndividualTelItem>>): Nullable<IndividualTelItem> => res?.Data)

// Get a partition.
export const getPartition = (
    telServerID: number,
    partitionID: number,
    ...partitionFields: string[]
): Promise<Nullable<PartitionValues>> =>
    requestGetJSON<APIData<PartitionValues>>(partitionURL(telServerID, partitionID, ...partitionFields), {
        cacheTime: CACHE_TIME
    }).then((res: JSONResponse<APIData<PartitionValues>>): Nullable<PartitionValues> => res?.Data)

// Get default reports for a given reporting level.
export const getDefaultReports = (
    reportingLevel: string,
    telSysType: Nullable<TelSysTypes>,
    telSysID?: number
): Promise<Nullable<SelectOptions<string>>> =>
    requestGetJSON<DefaultReportItem[]>(defaultReportsListURL(reportingLevel.replace(/\s/g, '_'), telSysID), {
        cacheTime: CACHE_TIME
    }).then((defaultReports: JSONResponse<DefaultReportItem[]>): Nullable<SelectOptions<string>> => {
        if (!defaultReports) return null

        const language: CLanguageHandler = CLanguageHandler.getInstance()
        language.loadBundle(ReportingDefaultReportSets.NAME)

        return defaultReports
            .sort((a: DefaultReportItem, b: DefaultReportItem) => a.Weight - b.Weight)
            .filter((report: DefaultReportItem): boolean => !IsVarEmpty(report))
            .map(({ ID, SelectedByDefault }: DefaultReportItem): SelectOption<string> => {
                const labelLanguage: string = language.getWithTelSysCheck(`DefaultSet_${ID}`, telSysType)
                const label: string = IsStringEmpty(labelLanguage) ? ID.replace(/_/g, ' ') : labelLanguage

                return {
                    label,
                    value: ID,
                    defaultSelected: SelectedByDefault
                }
            })
    })

export const getSimulationModes = (): Promise<Nullable<SelectOptions<string>>> =>
    requestGetJSON<string[]>(getSimulationModesURL()).then(
        (simulationModes: JSONResponse<string[]>): Nullable<SelectOptions<string>> =>
            simulationModes?.map(
                (mode: string): SelectOption<string> => ({
                    label: mode,
                    value: mode
                })
            )
    )

export const getLocales = (): Promise<Nullable<LocaleEntries>> =>
    requestGetJSON<LocaleEntries>(LOCALES_URL).then(
        (locales: JSONResponse<LocaleEntries>): Nullable<LocaleEntries> => locales
    )

export const getClientConfigurationsList = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): Promise<Nullable<ClientConfigurationsResponse>> =>
    requestGetJSON(getClientConfigurationListURL(telSysID, partitionID, ANY_DEVICE_VALUE, offset, limit, sort, search))

export const getCRMConfigurationsList = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    crmType?: string
): Promise<Nullable<CRMConfigurationsResponse>> =>
    requestGetJSON(
        getCRMConfigurationListURL(telSysID, partitionID, ANY_DEVICE_VALUE, offset, limit, sort, search, crmType)
    )

/**
 * Fetches the paginated telephony server list for a CME scope.
 *
 * @param cmeID - CME identifier used to scope the list query.
 * @param recursive - Whether child CMEs should be included in the list query.
 * @param offset - Optional zero-based pagination offset.
 * @param limit - Optional maximum number of rows to return.
 * @param sort - Optional sort expression in the format expected by the backend.
 * @param search - Optional search query.
 * @param listFilters - Optional column-aligned filters forwarded to the CME TelSys list API.
 * @param telSysFields - Telephony server fields to request; defaults to the admin list field set.
 * @returns Promise resolving to the paginated telephony server list response or `null`.
 */
export const getTelsysList = (
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    listFilters?: TelsysListAPIFilters,
    telSysFields: readonly string[] = TELSYS_ADMIN_LIST_FIELDS
): Promise<Nullable<TelsysListResponse>> => // NOSONAR
    requestGetJSON<TelsysListResponse>(
        telSysListURL(cmeID, recursive, offset, limit, sort, search, [...telSysFields], listFilters)
    )

/**
 * Fetches configuration change log (audit trail) rows for the admin list.
 *
 * @param query - Forwarded as query parameters on {@code GET /v1/admin/changeLog}.
 * @returns Promise resolving to the list payload or {@code null} when empty.
 */
export const getChangeLogList = (query: ChangeLogListQueryParams): Promise<Nullable<ConfigChangeLogListResponse>> =>
    requestGetJSON<ConfigChangeLogListResponse>(changeLogListURL(query), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Fetches in-memory log file status rows for the App v2 log management list.
 *
 * @param query - CME / TelSys scope, pagination, and optional search text.
 * @returns Promise resolving to the list payload.
 */
export const getLogManagementStatusList = (
    query: LogManagementStatusQueryParams
): Promise<Nullable<LogManagementListResponse>> =>
    requestGetJSON<LogManagementListResponse>(logManagementStatusURL(query), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

type LogManagementMutationBody = {
    readonly FileNames: readonly string[]
}

/**
 * Starts logging for the selected file names (admin scope enforced server-side).
 *
 * @param scope - CME / TelSys query scope.
 * @param fileNames - File names to start.
 */
export const postLogManagementStart = (
    scope: LogManagementScopeQueryParams,
    fileNames: readonly string[]
): Promise<JSONResponse<unknown>> =>
    requestJSON<unknown>(logManagementMutationURL('start', scope), {
        method: RESTMethods.POST,
        __INTENTIONALLY_CACHE_NON_GET__: true,
        body: { FileNames: fileNames } satisfies LogManagementMutationBody,
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Stops logging for the selected file names (admin scope enforced server-side).
 *
 * @param scope - CME / TelSys query scope.
 * @param fileNames - File names to stop.
 */
export const postLogManagementStop = (
    scope: LogManagementScopeQueryParams,
    fileNames: readonly string[]
): Promise<JSONResponse<unknown>> =>
    requestJSON<unknown>(logManagementMutationURL('stop', scope), {
        method: RESTMethods.POST,
        __INTENTIONALLY_CACHE_NON_GET__: true,
        body: { FileNames: fileNames } satisfies LogManagementMutationBody,
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Resets (deletes) log files for the selected file names (admin scope enforced server-side).
 *
 * @param scope - CME / TelSys query scope.
 * @param fileNames - File names to reset.
 */
export const postLogManagementReset = (
    scope: LogManagementScopeQueryParams,
    fileNames: readonly string[]
): Promise<JSONResponse<unknown>> =>
    requestJSON<unknown>(logManagementMutationURL('reset', scope), {
        method: RESTMethods.POST,
        __INTENTIONALLY_CACHE_NON_GET__: true,
        body: { FileNames: fileNames } satisfies LogManagementMutationBody,
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Fetches global logging settings and dropdown options for App v2 logging settings page.
 *
 * @returns Promise resolving to logging settings payload.
 */
export const getLoggingSettings = (): Promise<Nullable<LoggingSettingsResponse>> =>
    requestGetJSON<LoggingSettingsResponse>(loggingSettingsURL(), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Saves global logging settings for App v2 logging settings page.
 *
 * @param settings - Full logging settings payload.
 * @returns Promise resolving to REST response.
 */
export const putLoggingSettings = (settings: LoggingSettingsPutRequest): Promise<JSONResponse<unknown>> =>
    requestJSON<unknown>(loggingSettingsURL(), {
        method: RESTMethods.PUT,
        __INTENTIONALLY_CACHE_NON_GET__: true,
        body: settings,
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Fetches telephone module status rows for the App v2 tel module status list.
 *
 * @param query - CME / TelSys scope, pagination, and optional search text.
 * @returns Promise resolving to the list payload.
 */
export const getTelModuleStatusList = (
    query: TelModuleStatusQueryParams
): Promise<Nullable<TelModuleStatusListResponse>> =>
    requestGetJSON<TelModuleStatusListResponse>(telModuleStatusURL(query), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Fetches the paginated admin application user list for a TelSys / partition scope.
 *
 * @param telSysID - Telephone system ID path segment ({@code -1} means all accessible servers).
 * @param partitionIDInTelSys - Partition ID within the telephone system ({@code -1} means all partitions).
 * @param cmeID - CM Environment identifier forwarded as {@code CMEnvironmentID}.
 * @param recursive - Whether child CMEs should be included for non-root CME scopes.
 * @param offset - Zero-based pagination offset.
 * @param limit - Page size.
 * @param sort - Optional sort expression (for example {@code FullName:ASC}).
 * @param search - Optional search string (username and full name).
 * @param userTypeFilter - Optional legacy list filter id; omit for "all users".
 * @param licenceFilter - Optional comma separated reporting licence filter options; a row matches any of them.
 * @param activityFilter - Optional comma separated sign-in or activity filter options; a row matches any of them.
 * @param lockedOutFilter - Optional locked-out state to restrict to; omit to accept either state.
 * @param pendingDeletionFilter - True for only users awaiting deletion, false to exclude them, omit for both.
 * @param userFields - Optional user field names per row.
 * @returns Promise resolving to the list response or {@code null} when empty.
 */
export const getUsersList = (
    telSysID: string | number,
    partitionIDInTelSys: string | number,
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    userTypeFilter?: string,
    licenceFilter?: string,
    activityFilter?: string,
    lockedOutFilter?: boolean,
    pendingDeletionFilter?: boolean,
    userFields: readonly string[] = USER_ADMIN_LIST_FIELDS
): Promise<Nullable<UsersListResponse>> => // NOSONAR
    requestGetJSON<UsersListResponse>(
        usersAdminListURL(
            telSysID,
            partitionIDInTelSys,
            cmeID,
            recursive,
            offset,
            limit,
            sort,
            search,
            userTypeFilter,
            [...userFields],
            licenceFilter,
            activityFilter,
            lockedOutFilter,
            pendingDeletionFilter
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated Licence Management (fast-provisioning) list for a telephone system /
 * partition scope.
 *
 * @param telSysID - Telephone system ID.
 * @param partitionIDInTelSys - Partition ID within the telephone system.
 * @param cmeID - CM Environment ID to scope the query to (optional when empty).
 * @param recursive - When {@code true}, include descendant CMEs; defaults to {@code true}.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Optional sort expression.
 * @param search - Optional search string.
 * @returns Promise resolving to the list response or {@code null}/undefined when empty.
 */
export const getLicenceManagementList = (
    telSysID: string | number,
    partitionIDInTelSys: string | number,
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): Promise<Nullable<LicenceManagementListResponse>> =>
    requestGetJSON<LicenceManagementListResponse>(
        licenceManagementListURL(
            telSysID,
            partitionIDInTelSys,
            cmeID,
            recursive,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated CM Environment list for the current user scope.
 *
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Optional sort expression ({@code ID:ASC}, {@code Description:ASC}, etc.).
 * @param search - Optional search string.
 * @param cmeFields - Optional CME field names per row.
 * @param parentCmeId - Optional parent CME ID to restrict results to children of that CME.
 * @param recursive - When {@code true} with {@code parentCmeId}, include all descendant CMEs; when {@code false}, direct children only.
 *     When {@code parentCmeId} is set and this argument is omitted, the URL builder defaults to {@code true}.
 * @returns Promise resolving to the list response or {@code null}/undefined when empty.
 */
export const getCmeList = (
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    cmeFields?: readonly string[],
    parentCmeId?: string,
    recursive?: boolean
): Promise<Nullable<CmeListResponse>> =>
    requestGetJSON<CmeListResponse>(
        cmeListURL(offset, limit, sort, search, [...(cmeFields ?? CME_ADMIN_LIST_FIELDS)], parentCmeId, recursive)
    )

/**
 * Fetches the paginated partition list for a telephony server (admin API).
 *
 * @param telSysID - Telephony server ID.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Optional sort expression.
 * @param search - Optional search query.
 * @param partitionIDInTelSys - Optional partition self-filter.
 * @param licenceFilter - Optional partition licence filter.
 * @param partitionFields - Optional partition field names per row.
 * @returns Promise resolving to the partition list response or null when empty.
 */
export const getPartitionList = (
    telSysID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    partitionIDInTelSys?: string | number,
    licenceFilter?: string,
    syncTypeFilter?: string,
    statusFilter?: string,
    partitionFields: readonly string[] = PARTITION_ADMIN_LIST_FIELDS
): Promise<Nullable<PartitionListResponse>> => // NOSONAR
    requestGetJSON<PartitionListResponse>(
        partitionAdminListURL(
            telSysID,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search,
            partitionIDInTelSys,
            IsStringEmpty(licenceFilter) ? undefined : licenceFilter,
            [...partitionFields],
            IsStringEmpty(syncTypeFilter) ? undefined : syncTypeFilter,
            IsStringEmpty(statusFilter) ? undefined : statusFilter
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated ACD Agent list for a telephony server partition (internal API).
 *
 * @param telSysID - Telephony server ID.
 * @param partInTelSys - Partition ID within the telephone system.
 * @param monitoredOnly - When true, exclude agents with override monitoring.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code AgentNumber:ASC}, {@code Description:ASC}).
 * @param search - Optional search (agent number and description).
 * @param agentFields - Optional ACD agent field names per row.
 * @returns Promise resolving to the list response or null/undefined when empty.
 */
export const getAgentList = (
    telSysID: number,
    partInTelSys: number,
    monitoredOnly: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    filterAgentRange?: string,
    agentFields: readonly string[] = AGENT_ADMIN_LIST_FIELDS
): Promise<Nullable<AgentListResponse>> => // NOSONAR
    requestGetJSON<AgentListResponse>(
        agentsURL(
            telSysID,
            partInTelSys,
            monitoredOnly,
            [...agentFields],
            IsStringEmpty(search) ? undefined : search,
            offset,
            limit,
            sort,
            filterAgentRange
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated device list for a telephony server partition (admin API, SOAP-backed list).
 *
 * @param telSysID - Telephony server ID.
 * @param partitionID - Partition ID within the telephone system.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code DeviceNumber:ASC}, {@code DeviceTypeID:ASC}).
 * @param search - Optional search query.
 * @param deviceTypeFilter - Optional device type filter (e.g. [20, 21]).
 * @param deviceFields - Optional device field names per row.
 * @returns Promise resolving to the device list response or null when empty.
 */
export const getDeviceList = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    deviceTypeFilter?: string[],
    filterDeviceRange?: string,
    deviceFields: readonly string[] = DEVICE_ADMIN_LIST_FIELDS
): Promise<Nullable<DeviceListResponse>> => // NOSONAR
    requestGetJSON<DeviceListResponse>(
        devicesAdminListURL(
            telSysID,
            partitionID,
            false,
            deviceTypeFilter,
            [...deviceFields],
            IsStringEmpty(search) ? undefined : search,
            undefined,
            limit,
            offset,
            sort,
            filterDeviceRange
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated code list for a CME scope (internal API).
 *
 * @param cmeID - CME identifier.
 * @param recursive - Whether to include child CMEs.
 * @param telSysID - Optional TelSys ID to restrict scope.
 * @param partIDInTelSys - Optional partition ID in TelSys to restrict scope.
 * @param codeTypeFilter - Optional code type filter (e.g. comma-separated type IDs).
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}, {@code Code:ASC}).
 * @param search - Optional search query.
 * @param codeScopeFilter - Optional scope filter ({@code 1}=service-wide, {@code 2}=system-wide, {@code 10}=detached).
 * @param codeFields - Optional code field names per row.
 * @returns Promise resolving to the code list response or null when empty.
 */
export const getCodeList = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partIDInTelSys?: string | number,
    codeTypeFilter?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    codeScopeFilter?: string,
    codeFields: readonly string[] = CODE_ADMIN_LIST_FIELDS
): Promise<Nullable<CodeListResponse>> => // NOSONAR
    requestGetJSON<CodeListResponse>(
        codeListURL(
            cmeID,
            recursive,
            telSysID,
            partIDInTelSys,
            codeTypeFilter,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search,
            [...codeFields],
            codeScopeFilter
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated recording integration list for a CME scope (internal API).
 *
 * @param cmeID - CME identifier.
 * @param recursive - Whether to include child CMEs.
 * @param telSysID - Optional TelSys ID to restrict scope.
 * @param partIDInTelSys - Optional partition ID in TelSys to restrict scope.
 * @param callRecTypeFilter - Optional call recording integration type filter.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}).
 * @param search - Optional search query.
 * @param callRecScopeFilter - Optional scope filter ({@code 1}=service-wide, {@code 2}=system-wide, {@code 10}=detached).
 * @param callRecFields - Optional call recording integration field names per row.
 * @returns Promise resolving to the recording list response or null when empty.
 */
export const getRecordingList = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partIDInTelSys?: string | number,
    callRecTypeFilter?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    callRecScopeFilter?: string,
    callRecFields: readonly string[] = RECORDING_ADMIN_LIST_FIELDS
): Promise<Nullable<RecordingListResponse>> => // NOSONAR
    requestGetJSON<RecordingListResponse>(
        recordingListURL(
            cmeID,
            recursive,
            telSysID,
            partIDInTelSys,
            callRecTypeFilter,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search,
            [...callRecFields],
            callRecScopeFilter
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated directory entry list for a telephony server partition (internal API).
 *
 * @param telSysID - Telephony server ID.
 * @param partitionIDInTelSys - Partition ID within the telephone system (numeric path segment).
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}, {@code TelephoneNumber:ASC}).
 * @param search - Optional search query (telephone number and description).
 * @returns Promise resolving to the list response or null when empty.
 */
export const getDirectoryEntryList = (
    telSysID: string | number,
    partitionIDInTelSys: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): Promise<Nullable<DirectoryListResponse>> =>
    requestGetJSON<DirectoryListResponse>(
        directoryEntryListURL(
            telSysID,
            partitionIDInTelSys,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

/**
 * Fetches the paginated directory entry list for a CME scope (internal API), including multi-partition aggregation
 * when {@code partitionIDInTelSys} is omitted.
 *
 * @param cmeID - CME identifier.
 * @param recursive - Whether to include child CMEs (matches code list / TelSys list behaviour).
 * @param telSysID - Optional TelSys ID to restrict scope.
 * @param partitionIDInTelSys - Optional partition ID in TelSys; when omitted with a concrete {@code telSysID}, all partitions on that TelSys are included.
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}, {@code TelephoneNumber:ASC}).
 * @param search - Optional search query.
 * @returns Promise resolving to the list response or null when empty.
 */
export const getDirectoryEntryListForCME = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partitionIDInTelSys?: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): Promise<Nullable<DirectoryListResponse>> => // NOSONAR
    requestGetJSON<DirectoryListResponse>(
        directoryListForCMEURL(
            cmeID,
            recursive,
            telSysID,
            partitionIDInTelSys,
            offset,
            limit,
            sort,
            IsStringEmpty(search) ? undefined : search
        ),
        { errorOnNoContent: false, cacheTime: NO_CACHE_TIME }
    )

export const getReportEntityTypesAsSelectOptions = (): Promise<SelectOptions<number>> =>
    requestGetJSON<JSONResponse<ReportEntityTypes[]>>(getFilterEntityTypesURL).then(
        (res: JSONResponse<ReportEntityTypes[]>): SelectOptions<number> =>
            res?.map(
                (entityType: ReportEntityTypes): SelectOption<number> => ({
                    label: entityType.description,
                    value: entityType.id
                })
            ) ?? []
    )

/**
 * Fetches the users for a specific telsys and/or partition.
 *
 * @param telsys - Telephony system identifier.
 * @param partition - (Optional) Partition identifier.
 * @param userFields - (Optional) Array of user fields to include in the response.
 *
 * @return A promise that resolves to an array of AdminUserResponse or null.
 */
export const fetchUsers = (
    telsys: string | number,
    partition?: string | number,
    userFields?: string[]
): Promise<Nullable<InternalUserResponse[]>> =>
    requestGetJSON<APIListData<InternalUserResponse>>(
        usersURLByTelSysPartition(telsys, isVarNotEmpty(partition) ? partition : ALL_VALUE, userFields),
        { cacheTime: NO_CACHE_TIME }
    ).then((res: JSONResponse<APIListData<InternalUserResponse>>): Nullable<InternalUserResponse[]> => res?.Data?.Items)

/**
 * Counts the number of users for a specific telsys and/or partition.
 *
 * @param telsys - Telephony system identifier.
 * @param partition - (Optional) Partition identifier.
 *
 * @return A promise that resolves to the count of users or undefined.
 */
export const countLicencedUsersForTelSysPartition = (
    telsys: string | number,
    partition?: string | number
): Promise<Definable<number>> =>
    fetchUsers(telsys, partition, ['ReportingAccess', 'PendingDeletion']).then(
        (users: Nullable<InternalUserResponse[]>): Definable<number> => {
            if (IsVarEmpty(users)) {
                return 0
            }

            return (
                users.filter(
                    (user: InternalUserResponse): boolean =>
                        !user.PendingDeletion && user.ReportingAccess !== ReportingAccessLevels.NONE
                )?.length ?? 0
            )
        }
    )
